import React from 'react';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { toast } from 'react-hot-toast';
import { useParams } from 'react-router-dom';

import { defaultToast, ToastID } from 'ui/base/toast';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { UnreachableFunction } from 'ui/base/types';
import { Link } from 'ui/base/link';
import { BackButton } from 'ui/base/button/back_button';
import { StandardPage } from 'ui/base/page/standard_page/standard_page';

import { SwitchInput } from 'ui/standard/input/switch_input';

import { togglePullToRefresh } from 'services/base/ipc';
import { ResultKind } from 'services/base/types';
import { BasePath } from 'services/base/page_path';

import { VPNServerAppStatus } from 'services/accounts/protos/vpnserver_pb';

import { IPangolinDomain, VPNClient, VPNServer } from 'services/pangolin/types';
import { usePangolinDomain } from 'services/pangolin/pangolin_domain';
import { hostDisplayDeviceType } from 'services/pangolin/hosts_service';
import { HostDeviceType } from 'services/pangolin/protos/hosts_pb';
import { getVPNClientHost } from 'services/pangolin/vpn_clients_service';

import { Redirect } from 'pages/core/redirect/redirect';

import {
  themedDeviceTypeIconClassStyles,
  getDeviceTypeIcon,
} from 'assets/enums/device_types/device_types';

import { VPNServerStore, VPNClientStore } from './list_vpn_clients_store';

import Messages from './list_vpn_clients_messages';
import baseStyles from './list_vpn_clients.module.css';

type ListVPNClientsStyles = {
  row: string;
  backButtonContainer: string;
  titleText: string;
  banner: string;
  bannerImage: string;
  serverHeader: string;
  serverBody: string;
  serverText: string;
  listHeader: string;
  listHeaderText: string;
  listBody: string;
  listItem: string;
  plusIcon: string;
  link: string;
  client: string;
  clientIconContainer: string;
  clientDetailsColumn: string;
  clientName: string;
  clientStatus: string;
  clientToggleContainer: string;
  deviceIcon: string;
};

const themedStyles: ThemedStyles<ListVPNClientsStyles> = {
  [Theme.DAYLIGHT]: {
    row: baseStyles.baseRow,
    backButtonContainer: baseStyles.baseBackButtonContainer,
    titleText: baseStyles.daylightTitleText,
    banner: baseStyles.banner,
    bannerImage: baseStyles.daylightBannerImage,
    serverHeader: baseStyles.daylightServerHeader,
    serverBody: baseStyles.serverBody,
    serverText: baseStyles.daylightServerText,
    listHeader: baseStyles.listHeader,
    listHeaderText: baseStyles.daylightListHeaderText,
    listBody: baseStyles.listBody,
    listItem: baseStyles.listItem,
    plusIcon: baseStyles.daylightPlusIcon,
    link: baseStyles.daylightLink,
    client: baseStyles.client,
    clientIconContainer: baseStyles.clientIconContainer,
    clientDetailsColumn: baseStyles.clientDetailsColumn,
    clientName: baseStyles.daylightClientName,
    clientStatus: baseStyles.daylightClientStatus,
    clientToggleContainer: baseStyles.clientToggleContainer,
    deviceIcon: baseStyles.deviceIcon,
  },
  [Theme.MIDNIGHT]: {
    row: baseStyles.baseRow,
    backButtonContainer: baseStyles.baseBackButtonContainer,
    titleText: baseStyles.midnightTitleText,
    banner: baseStyles.banner,
    bannerImage: baseStyles.midnightBannerImage,
    serverHeader: baseStyles.midnightServerHeader,
    serverBody: baseStyles.serverBody,
    serverText: baseStyles.daylightServerText,
    listHeader: baseStyles.listHeader,
    listHeaderText: baseStyles.midnightListHeaderText,
    listBody: baseStyles.listBody,
    listItem: baseStyles.listItem,
    plusIcon: baseStyles.midnightPlusIcon,
    link: baseStyles.midnightLink,
    client: baseStyles.client,
    clientIconContainer: baseStyles.clientIconContainer,
    clientDetailsColumn: baseStyles.clientDetailsColumn,
    clientName: baseStyles.midnightClientName,
    clientStatus: baseStyles.midnightClientStatus,
    clientToggleContainer: baseStyles.clientToggleContainer,
    deviceIcon: baseStyles.deviceIcon,
  },
};

export function getVPNClientStatusText(client: VPNClient): string {
  if (client.isEnabled === false) {
    return Messages.statusDisabled();
  }

  switch (client.status) {
    case VPNServerAppStatus.VPNSERVER_APP_STATUS_UNCLAIMED:
      return Messages.statusUnclaimed();
    case VPNServerAppStatus.VPNSERVER_APP_STATUS_ONLINE:
      return Messages.statusOnline();
    case VPNServerAppStatus.VPNSERVER_APP_STATUS_OFFLINE:
      return Messages.statusOffline();
    case VPNServerAppStatus.VPNSERVER_APP_STATUS_DISABLED:
      return Messages.statusDisabled();
    default:
      return UnreachableFunction(client.status);
  }
}


type VPNClientComponentProps = {
  domain: IPangolinDomain;
  client: VPNClient;
};

const VPNClientComponent = observer(({ domain, client }: VPNClientComponentProps) => {
  const styles = useStyles(themedStyles);
  const deviceIcons = useStyles(themedDeviceTypeIconClassStyles);

  const [store, setStore] = React.useState(() => new VPNClientStore(client));
  React.useEffect(() => { setStore(new VPNClientStore(client)); }, [client]);

  const handleToggle = React.useCallback(async() => {
    const result = await store.handleToggle(domain);
    if (result.kind === ResultKind.OK) {
      toast.dismiss(ToastID.ERROR_ACTION_PANGOLIN_OFFLINE);
      toast.dismiss(ToastID.ERROR_ACTION_PLAN_REQUIRED);
      toast.dismiss(ToastID.ERROR_ACTION_GENERIC);
      return true;
    } else if (result.kind === ResultKind.USER_ERROR && result.status === 402) {
      defaultToast(ToastID.ERROR_ACTION_PLAN_REQUIRED);
      return false;
    } else if (result.kind === ResultKind.SYSTEM_ERROR && result.status === 502) {
      defaultToast(ToastID.ERROR_ACTION_PANGOLIN_OFFLINE);
      return false;
    } else {
      defaultToast(ToastID.ERROR_ACTION_GENERIC);
      return false;
    }
  }, [store, domain]);

  const host = getVPNClientHost(domain, client);
  const host_type = host != null ? hostDisplayDeviceType(host) : HostDeviceType.H_VPN;
  const host_icon_cls = getDeviceTypeIcon(deviceIcons, host_type);

  return (
    <div className={styles.client}>
      <div className={classNames(styles.deviceIcon, host_icon_cls)}/>
      <Link
        path={{ kind: BasePath.VPN_EDIT, pangolinID: domain.id, clientID: client.id }}
        className={styles.clientDetailsColumn}
      >
        <div className={styles.clientName}>
          {client.name}
        </div>
        <div className={styles.clientStatus}>
          {getVPNClientStatusText(client)}
        </div>
      </Link>
      <div className={styles.clientToggleContainer}>
        <SwitchInput store={store.enabled} onChange={handleToggle}/>
      </div>
    </div>
  );
});

type BaseListVPNClientsListProps = {
  domain: IPangolinDomain;
  server: VPNServer;
};

export const BaseListVPNClientsList = observer(({ domain, server }: BaseListVPNClientsListProps) => {
  const styles = useStyles(themedStyles);

  const [store, setStore] = React.useState(() => new VPNServerStore(server));
  React.useEffect(() => { setStore(new VPNServerStore(server)); }, [server]);

  const handleScroll = React.useCallback((event: React.UIEvent<HTMLElement>) => {
    togglePullToRefresh(event.currentTarget.scrollTop === 0);
  }, []);

  const handleToggle = React.useCallback(async() => {
    const result = await store.handleToggle(domain);
    if (result.kind === ResultKind.OK) {
      toast.dismiss(ToastID.ERROR_ACTION_PANGOLIN_OFFLINE);
      toast.dismiss(ToastID.ERROR_ACTION_PLAN_REQUIRED);
      toast.dismiss(ToastID.ERROR_ACTION_GENERIC);
      return true;
    } else if (result.kind === ResultKind.USER_ERROR && result.status === 402) {
      defaultToast(ToastID.ERROR_ACTION_PLAN_REQUIRED);
      return false;
    } else if (result.kind === ResultKind.SYSTEM_ERROR && result.status === 502) {
      defaultToast(ToastID.ERROR_ACTION_PANGOLIN_OFFLINE);
      return false;
    } else {
      defaultToast(ToastID.ERROR_ACTION_GENERIC);
      return false;
    }
  }, [store, domain]);

  React.useEffect(() => {
    togglePullToRefresh(true);
    return () => {
      togglePullToRefresh(false);
    };
  }, []);

  return (
    <StandardPage onScroll={handleScroll}>
      <div className={styles.row}>
        <div className={styles.backButtonContainer}>
          <BackButton/>
        </div>
        <div className={styles.titleText}>
          {Messages.title()}
        </div>
      </div>

      <div className={styles.banner}>
        <div className={styles.bannerImage}/>
      </div>

      <div className={styles.serverHeader}>
        {Messages.serverHeader()}
      </div>

      <div className={styles.serverBody}>
        <div className={styles.serverText}>
          {Messages.serverEnabled()}
        </div>
        <SwitchInput store={store.enabled} onChange={handleToggle}/>
      </div>

      <div className={styles.listHeader}>
        <div className={styles.listHeaderText}>
          {Messages.listHeader()}
        </div>
        <Link path={{ kind: BasePath.VPN_ADD, pangolinID: domain.id }} className={styles.link}>
          <div className={styles.plusIcon}/>
        </Link>
      </div>

      <div className={styles.listBody}>
        {domain.vpnClients.map((client) => (
          <VPNClientComponent
            key={client.id}
            domain={domain}
            client={client}
          />
        ))}
      </div>
    </StandardPage>
  );
});

export const ListVPNClientsList = observer(() => {
  const { pangolinID } = useParams<{ pangolinID: string }>();
  if (!pangolinID) return <Redirect />;

  const domain = usePangolinDomain(pangolinID);
  if (!domain) return <Redirect />;

  const server = domain.vpnServer;

  return (
    <BaseListVPNClientsList
      domain={domain}
      server={server}
    />
  );
});
